import React, { useEffect, useState } from "react";
import "./patientFullView.css";
import Widgets from "../../widgets";
import PatientPersonalInfo from "./patientPersonalInfo";
import PatientAddressView from "./patientAddressView";
import PatientIdView from "./patientIdView";
import PatientInsuranceView from "./patientInsuranceView";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getSingleVPfp } from "../../modules/patientProfile/patientProfileThunk";
import ToastPopup from "../../widgets/Toast/toastPopup";
import { resetGetSingleVPfp } from "../../modules/patientProfile/patientProfileSlice";
import strings from "../../utils/strings";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const PatientFullView = ({ setSelectedView, userId }) => {
  const dispatch = useDispatch();
  const { getSingleVPfpErr, getSingleVPfpSuccess } = useSelector((state) => state.patientProfile);
  const { t } = useTranslation();
  const breadcrumbs = ["patientProfiles", "patientDetails"];
  const paths = [{ path: "/patient-profile", onClick: () => setSelectedView("patientProfileList") }, { path: strings.navigation.patientProfile }];
  const [userInfo, setuserInfo] = useState({});
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [latestUpdateTime, setLatestUpdateTime] = useState(null);

  useEffect(() => {
    if (getSingleVPfpErr) {
      ToastPopup.error(getSingleVPfpErr);
      setIsPageLoading(false);
    }

    const maybeProcess = async () => {
      if (getSingleVPfpSuccess && getSingleVPfpSuccess.status === "success") {
        console.log("Full API Response:", getSingleVPfpSuccess);
        console.log("Patient Details:", getSingleVPfpSuccess.data?.patientDetails);
        setIsPageLoading(true);
        setuserInfo(getSingleVPfpSuccess.data);
        setIsPageLoading(false);
        
        const data = getSingleVPfpSuccess.data;
        if (data.insurances && Array.isArray(data.insurances)) {
          const insuranceUpdateTimes = data.insurances
            .map((ins) => (ins.updatedAt ? new Date(ins.updatedAt).getTime() : null))
            .filter((ts) => ts !== null);

          if (insuranceUpdateTimes.length > 0) {
            const latestTime = new Date(Math.max(...insuranceUpdateTimes));
            setLatestUpdateTime(latestTime);
            console.log("Latest Insurance Update Time:", latestTime);
          } else {
            setLatestUpdateTime(null);
          }
        } else {
          setLatestUpdateTime(null);
        }
      }
      dispatch(resetGetSingleVPfp());
    };
    maybeProcess();
  }, [getSingleVPfpSuccess, getSingleVPfpErr]);

  useEffect(() => {
    dispatch(getSingleVPfp({ userId }));
  }, [dispatch, userId]);

  const downloadPDF = async () => {
  setIsPdfExporting(true);

  const elementsToHide = [
    { selector: ".pdl-container > div:first-child", element: null, originalDisplay: "" },
    { selector: ".department-heading.pdl > div:first-child", element: null, originalDisplay: "" },
    { selector: ".pdl-buttons > button", element: null, originalDisplay: "" }
  ];

  try {
    elementsToHide.forEach(item => {
      item.element = document.querySelector(item.selector);
      if (item.element) {
        item.originalDisplay = item.element.style.display;
        item.element.style.display = "none";
      }
    });

    await new Promise(resolve => setTimeout(resolve, 100));

    const allImages = document.querySelectorAll(".pdl-container img");
    await Promise.all(Array.from(allImages).map(img => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise(resolve => {
        const timeout = setTimeout(resolve, 3000);
        img.onload = () => { clearTimeout(timeout); resolve(); };
        img.onerror = () => { clearTimeout(timeout); resolve(); };
      });
    }));

    const input = document.querySelector(".pdl-container");
    if (!input) throw new Error("Container not found");

    const canvas = await html2canvas(input, { 
      scale: 1.2,
      useCORS: true,
      logging: false,
      allowTaint: false,
      backgroundColor: "#ffffff"
    });
    
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error("Canvas is empty");
    }

    const hnNumber = userInfo.patientDetails?.hnNumber || "patient";
    const fileName = `${hnNumber}-profile.pdf`;

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    
    const imgWidth = pageWidth - 2 * margin;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    const imageData = canvas.toDataURL("image/png");
    
    if (imgHeight <= pageHeight - 2 * margin) {
      pdf.addImage(imageData, "PNG", margin, margin, imgWidth, imgHeight, undefined, "FAST");
    } else {
      const scale = (pageHeight - 2 * margin) / imgHeight;
      pdf.addImage(imageData, "PNG", margin, margin, imgWidth * scale, imgHeight * scale, undefined, "FAST");
    }
    
    pdf.save(fileName);
  } catch (err) {
    console.error("PDF export error:", err);
    ToastPopup.error(t("pdfExportError") || "Failed to export PDF. Please try again.");
  } finally {
    elementsToHide.forEach(item => {
      if (item.element) {
        item.element.style.display = item.originalDisplay;
      }
    });
    setIsPdfExporting(false);
  }
};

  return (
    <div className="pdl-container">
      {isPageLoading && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
          <Widgets.LoadingContainer />
        </div>
      )}
      {/* {isPdfExporting && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "all" }}>
          <Widgets.LoadingContainer />
        </div>
      )} */}
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading pdl">
        <div>{t("profile")}</div>
        <div className="pdl-buttons">
          <button onClick={downloadPDF} className="pdl-btn verified" disabled={isPdfExporting} style={{ border: "none" }}>
            {t("exportPDF")}
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {userInfo.patientDetails?.userVerifiedOn && (
              <div className="pfv-verfiedOn">
                {t("verifiedOn")}: {moment(userInfo.patientDetails?.userVerifiedOn).format("DD/MM/YYYY HH:mm")}
              </div>
            )}
            {latestUpdateTime && (
              <div className="pfv-verfiedOn">
                {t("latestUpdated")}: {moment(latestUpdateTime).format("DD/MM/YYYY HH:mm")}
              </div>
            )}
          </div>
          <div className="pdl-btn verified pav-status">{t("verified")}</div>
        </div>
      </div>
      <PatientPersonalInfo data={userInfo.patientDetails} />
      <PatientAddressView address={userInfo.address} />
      <PatientIdView data={userInfo.nationalIds} />
      <PatientInsuranceView insurance={userInfo} />
    </div>
  );
};

export default PatientFullView;
